'use client';

import { type StepperRef, useIntl, type UserRequirement } from '@capsicum/common';
import { BaseCheckboxInput, RadioButtonInput, Stepper } from '@capsicum/common-web';
import { uniqueRequirementAtom, useDomesticRinggitBorrowing } from '@capsicum/features/regulations/shared';
import { useSetAtom } from 'jotai';
import { useRef } from 'react';
import styled from 'styled-components';

export type DomesticRinggitBorrowingStatementProps = {
	isForcedRequirement?: boolean;
	isUniqueRequirement?: boolean;
	onDismiss: () => void;
	requirement: UserRequirement;
};

export const DomesticRinggitBorrowingStatement = ({
	isForcedRequirement,
	isUniqueRequirement,
	onDismiss,
	requirement
}: DomesticRinggitBorrowingStatementProps) => {
	const { $t } = useIntl();
	const setShowRequirementPage = useSetAtom(uniqueRequirementAtom);

	const {
		disabledFirstButton,
		domesticRinggitBorrowingOptions,
		domesticRinggitBorrowingSelected,
		domesticRinggitBorrowingTextSelected,
		hasDomesticRinggitBorrowing,
		investedOptions,
		isDeclarationConfirmed,
		isLoading,
		latestAcceptedAgreementLink,
		onSelectDomesticRinggitBorrowing,
		onSubmitStatement,
		policyNoticeLink,
		selectedInvested,
		setCurrentStep,
		setIsDeclarationConfirmed,
		setSelectedInvested
	} = useDomesticRinggitBorrowing({ requirement });

	const stepperRef = useRef<StepperRef>(null);

	const Step1 = () => {
		return (
			<S.ContentContainer>
				<S.MainContent>
					<S.Title>{$t('requirements.common.title')}</S.Title>
					<S.Subtitle>{$t('domesticRinggitBorrowing.modal.subtitle')}</S.Subtitle>
					<S.RadioButtonsContainer>
						{domesticRinggitBorrowingOptions.map(item => (
							<RadioButtonInput
								checked={item.id === domesticRinggitBorrowingSelected?.id}
								item={item}
								key={item.id}
								pressableLabels
								testID={item.testID}
								onPress={() => onSelectDomesticRinggitBorrowing(item)}
							/>
						))}
					</S.RadioButtonsContainer>
					<S.Description>
						{$t<JSX.Element>('domesticRinggitBorrowing.modal.description', {
							policyLink: chunks => (
								<S.Link
									data-testid="domestic-ringgit-borrowing-modal-policy-link"
									href={policyNoticeLink}
									target="_blank"
								>
									{chunks}
								</S.Link>
							),
							termsLink: chunks => (
								<S.Link
									data-testid="domestic-ringgit-borrowing-modal-terms-link"
									href={latestAcceptedAgreementLink || ''}
									target="_blank"
								>
									{chunks}
								</S.Link>
							)
						})}
					</S.Description>
				</S.MainContent>
			</S.ContentContainer>
		);
	};

	const Step2 = () => {
		return (
			<S.ContentContainer>
				<S.MainContent>
					<S.Description>{$t('domesticRinggitBorrowing.selectedLabel')}</S.Description>
					<S.DescriptionBold>{domesticRinggitBorrowingTextSelected}</S.DescriptionBold>
					{hasDomesticRinggitBorrowing && (
						<>
							<S.RadioButtonsContainer>
								{investedOptions.map(item => (
									<RadioButtonInput
										checked={item.id === selectedInvested}
										item={item}
										key={item.id}
										pressableLabels
										testID={item.testId || ''}
										onPress={() => setSelectedInvested(item.id)}
									/>
								))}
							</S.RadioButtonsContainer>
							<S.Description>
								{$t<JSX.Element>('domesticRinggitBorrowing.notice', {
									link: chunks => (
										<S.Link
											data-testid="domestic-ringgit-borrowing-link"
											href={policyNoticeLink}
											target="_blank"
										>
											{chunks}
										</S.Link>
									)
								})}
							</S.Description>
						</>
					)}
					<BaseCheckboxInput
						testID="domestic-ringgit-borrowing-checkbox"
						value={isDeclarationConfirmed}
						onChange={setIsDeclarationConfirmed}
					>
						<S.CheckboxLabel>{$t('domesticRinggitBorrowing.confirmDeclaration')}</S.CheckboxLabel>
					</BaseCheckboxInput>
				</S.MainContent>
			</S.ContentContainer>
		);
	};

	const submitStatement = () => {
		onSubmitStatement(() => {
			if (isUniqueRequirement) setShowRequirementPage(false);
		});
	};

	return (
		<S.Wrapper>
			<Stepper
				customBackNavigation={onDismiss}
				disabledFirstButton={disabledFirstButton}
				isLoading={isLoading}
				onlyButtonsNavigation={isForcedRequirement}
				ref={stepperRef}
				showStepsNavigation
				steps={[Step1, Step2]}
				testID="domestic-ringgit-borrowing-stepper"
				onStepIndexChange={index => setCurrentStep(index)}
				onSubmitSteps={submitStatement}
			/>
		</S.Wrapper>
	);
};

namespace S {
	export const Wrapper = styled.div`
		display: flex;
		flex: 1;
		flex-direction: column;
		padding: ${({ theme }) => `${theme.spacings.medium} 0 0`};
		${({ theme }) => theme.breakpoints.queries.below.tabletSmallDesktop} {
			padding: ${({ theme }) => `${theme.spacings.medium} ${theme.spacings.small} 0`};
		}
	`;

	export const Title = styled.span`
		${({ theme }) => theme.fonts.headings.title6};
		margin-bottom: ${({ theme }) => theme.spacings.xxsmall};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const Subtitle = styled.span`
		${({ theme }) => theme.fonts.text.text3};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const ContentContainer = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.xlarge};
		${({ theme }) => theme.border.radius.medium};
		${({ theme }) => theme.breakpoints.queries.below.mobile} {
			margin-bottom: ${({ theme }) => theme.spacings.xxxxlarge};
		}
	`;

	export const MainContent = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.large};
	`;

	export const RadioButtonsContainer = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.large};
		white-space: pre-line;
	`;

	export const Description = styled.span`
		${({ theme }) => theme.fonts.text.text3};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const DescriptionBold = styled.span`
		margin-top: -${({ theme }) => theme.spacings.small};
		margin-bottom: ${({ theme }) => theme.spacings.large};
		${({ theme }) => theme.fonts.headings.title6};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const CheckboxLabel = styled.span`
		${({ theme }) => theme.fonts.text.text3};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const Link = styled.a`
		color: ${({ theme }) => theme.colors.text.brand.enabled};
		${({ theme }) => theme.fonts.text.text3};
		text-decoration: none;
	`;

	export const EmptyContainer = styled.div`
		flex: 1;
		background-color: ${({ theme }) => theme.colors.backgrounds.main.screen};
	`;
}
